import React, { ChangeEvent, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { CircularProgress, FormLabel, TextField } from "@mui/material";
import _ from "lodash";
import { useApplicationDefaultLanguage, useSelectedBackofficeLanguages } from "../../../utils/languageHooks";
import { TranslationEndpointParams, TranslationsStorage, TranslationTextField } from "./TranslatableEditor.d";
import TranslationWrapper from "../TranslationWrapper/TranslationWrapper";
import axios from "axios";
import { LanguageAvailableInConfig, languagesAvailableInConfig } from "./translations_definitions";
import { LanguageSelect } from "./LanguageSelect";
import SaveButton from "../SaveButton/SaveButton";
import Editor from "../../views/SunEditor";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  formItem: {
    marginBottom: 8,
  },
}));

export function addMissingLanguages(storage: TranslationsStorage, translation_keys: ReadonlyArray<string>) {
  languagesAvailableInConfig.forEach(({ value }) => {
    if (!storage.hasOwnProperty(value)) {
      storage[value] = {};

      for (const name of translation_keys) {
        storage[value][name] = "";
      }
    }
  });
}

export function useTranslations(
  { id, model_name }: TranslationEndpointParams,
  translation_keys: string[],
  setLoading: (arg: boolean) => void
): [TranslationsStorage, Function] {
  const [translations, setTranslations] = useState<TranslationsStorage>({});

  useEffect(() => {
    axios
      .post(`/core/html_translations/${model_name}`, {
        id,
        translation_keys,
      })
      .then((response) => {
        const translations = response.data.data;
        // empty response is array instead of object
        if (_.isArray(translations)) {
          const t = {};
          addMissingLanguages(t, translation_keys);
          setTranslations(t);
        } else {
          addMissingLanguages(translations, translation_keys);
          setTranslations(translations);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  return [translations, setTranslations];
}

/**
 * An HTML editor based on TinyMCE to make customizable HTML content for various purposes
 *
 * contentName and name param of textFields represent `translation_key` in the `html_translations` table
 *
 * @see Betweenorder.jsx
 * @example How to use translatable editor
 * <TranslatableEditor
 *    contentName="betweenOrderContent"
 *    endpointParams={{
 *        id: vestiging_id,
 *        model_name: "vestiging",
 *    }}
 *    textFields={[
 *    {
 *      name: "betweenOrderTitle",
 *      label: { tKey: "betweenOrder.translatableEditor.label.title", tDefault: "Title" },
 *    },
 *    {
 *      name: "betweenOrderOkText",
 *      label: {
 *        tKey: "betweenOrder.translatableEditor.label.ok",
 *        tDefault: "Ok button text",
 *      },
 *    },
 *    {
 *      name: "betweenOrderCancelText",
 *      label: {
 *        tKey: "betweenOrder.translatableEditor.label.cancel",
 *        tDefault: "Cancel button text",
 *    },
 *  },
 * }/>
 *
 * @see HtmlTranslationService Related functionality in backend for html translations
 *
 * @see HtmlTranslationController Controller
 *
 * @see HasHtmlTranslations Trait on models defining that model has morphic html_translations
 *
 * @param contentName The translation_key for the html content from the html editor
 * @param endpointParams Config to fetch and save translations from HasTranslatable models { model_name: str, model_id: int}
 * @param textFields Array of additional text fields that need to be translated along with the html content
 *
 * @returns {JSX.Element}
 * @constructor
 */
export default function TranslatableEditor({
  contentName,
  endpointParams,
  textFields = [],
}: {
  contentName: string;
  endpointParams: TranslationEndpointParams;
  textFields?: TranslationTextField[];
}) {
  const translation_keys = [...textFields.map(({ name }) => name), contentName];
  const [loading, setLoading] = useState<boolean>(true);

  // Save button
  const [disabled, setDisabled] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);

  const defaultLanguage = useApplicationDefaultLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageAvailableInConfig | undefined>(
    languagesAvailableInConfig.find(({ value }) => value === defaultLanguage)
  );
  if (selectedLanguage === undefined) {
    throw new TypeError(`language: "${defaultLanguage}" should exist in languagesAvailableInConfig`);
  }
  const { classes } = useStyles();

  const configLanguages = useSelectedBackofficeLanguages(defaultLanguage);
  const activeLanguages = languagesAvailableInConfig
    .filter(({ value }) => configLanguages.includes(value))
    .map(({ value }) => value);

  const [storage, setStorage] = useTranslations(endpointParams, translation_keys, setLoading);
  const [fields, setFields] = useState<string[]>([]);

  useEffect(() => {
    setFields(
      textFields.map((tf) => {
        return storage?.[selectedLanguage.value]?.[tf.name] ?? "";
      })
    );
  }, [storage]);

  const save = () => {
    setIsSaving(true);
    axios
      .post(`/core/html_translations/${endpointParams.model_name}/save`, {
        id: endpointParams.id,
        translations: storage,
      })
      .catch((res) => {
        setIsValid(true);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const changeLanguage = (e: ChangeEvent<{ name?: string; value: unknown }>) => {
    const newLang = languagesAvailableInConfig.find(({ value }) => value === e.target.value);
    if (newLang === undefined) {
      throw new TypeError(
        `language: "${e.target.value}" should exist in languagesAvailableInConfig: ${languagesAvailableInConfig}`
      );
    }
    // @ts-ignore
    setSelectedLanguage(newLang);
    setFields(
      textFields.map((tf) => {
        return storage?.[newLang.value]?.[tf.name] ?? "";
      })
    );
  };

  const renderHeader = () => (
    <>
      <SaveButton disabled={disabled} isSubmitting={isSaving} isValid={isValid} onClick={() => save()} />
      <FormLabel className={classes.formItem}>
        <TranslationWrapper tDefault={"Language"} tKey={"translations.htmleditor.label.title"} />
      </FormLabel>
      <LanguageSelect
        allowedLanguages={activeLanguages.length > 0 ? activeLanguages : [defaultLanguage]}
        language={selectedLanguage.value}
        onChange={changeLanguage}
      />
    </>
  );

  const renderFields = () =>
    textFields.map(({ name, label: { tDefault, tKey } }, idx) => {
      const value = fields[idx];
      return (
        <>
          <FormLabel className={classes.formItem}>
            <TranslationWrapper tDefault={tDefault} tKey={tKey} />
          </FormLabel>
          <TextField
            className={classes.formItem}
            onChange={(e) => {
              const newStorage = { ...storage };
              newStorage[selectedLanguage.value][name] = e.target.value;
              setStorage(newStorage);
              setDisabled(false);
            }}
            value={value}
          />
        </>
      );
    });

  return loading ? (
    <CircularProgress color={"primary"} style={{ margin: "8px auto" }} />
  ) : (
    <div className={classes.root}>
      {renderHeader()}
      {renderFields()}
      {(activeLanguages.length > 0 ? activeLanguages : [defaultLanguage]).map((language) => {
        const activelangStorage = storage[language];
        const editorName = `${contentName}_${language}`;
        const isActive = selectedLanguage?.value === language;

        const styles = { display: "none" };
        if (isActive) {
          styles.display = "initial";
        }

        return (
          <div style={styles}>
            <Editor
              defaultValue={activelangStorage[contentName]}
              key={contentName}
              onChange={(changedContent) => {
                const newStorage = { ...storage };
                newStorage[language][contentName] = changedContent;
                setStorage(newStorage);
                setDisabled(false);
              }}
              textareaName={editorName}
            />
          </div>
        );
      })}
    </div>
  );
}
